import { useCustomFileUpload, UploadConfig } from './useCustomFileUpload';

// Constants for file validation
export const VALID_EXTENSIONS = ['.xml', '.musicxml', '.mxl', '.pdf', '.PDF'];
export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

// Image page bundles are a separate upload path (OMR flow). These mirror the
// backend's `ALLOWED_IMAGE_MAGIC` extensions, `IMAGE_UPLOAD_MAX_PAGES` (15),
// and `IMAGE_UPLOAD_MAX_BYTES_PER_PAGE` (10MB) so we can give fast, friendly
// feedback before starting a long upload the server would reject anyway.
export const VALID_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'];
/** MIME types for the same formats. A drag shows types but not names. */
export const VALID_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const MAX_IMAGE_PAGES = 15;
export const MAX_IMAGE_BYTES_PER_PAGE = 10 * 1024 * 1024; // 10MB

/** One consistent message for hitting the image page-count limit, shared by
 *  the upload box and the reorder page. */
export const imagePageLimitMessage = (limit: number): string =>
  limit <= 1
    ? 'Free and guest uploads accept a single page image. Subscribe or add credits to upload multi-page scans.'
    : `You can upload up to ${limit} page images.`;

const getExtension = (fileName: string): string =>
  fileName.toLowerCase().split('.').pop() ?? '';

/** True when the file's extension is one the image (OMR) upload path accepts. */
export const isImageFile = (file: File): boolean =>
  VALID_IMAGE_EXTENSIONS.includes(`.${getExtension(file.name)}`);

// Helper function for file validation
export const validateFile = (file: File) => {
  const fileExtension = getExtension(file.name);
  const isValidExtension = fileExtension
    ? VALID_EXTENSIONS.includes(`.${fileExtension}`)
    : false;
  const isValidSize = file.size <= MAX_FILE_SIZE;
  return { isValidExtension, isValidSize };
};

// Specialized hook for MusicXML fingering uploads
const useFingeringUpload = (isAuthenticated: boolean) => {
  const fingeringConfig: UploadConfig = {
    endpoint: '/fingering/upload/{projectId}',
    imagesEndpoint: '/fingering/upload-images/{projectId}',
    validExtensions: VALID_EXTENSIONS,
    acceptTypes:
      '.xml, .XML, .musicxml, .MUSICXML, .mxl, .MXL, .pdf, .PDF, application/octet-stream',
    invalidFileMessage:
      'Invalid file format. Please select a .xml, .musicxml, .mxl, .pdf, or .PDF file.',
    maxFileSize: MAX_FILE_SIZE,
    invalidFileSizeMessage:
      'File size exceeds 10MB limit. Please select a smaller file.',
    requiresProject: true, // Fingering flow requires project creation
  };

  const customUpload = useCustomFileUpload(isAuthenticated, fingeringConfig);

  return {
    ...customUpload,
    uploadFileDirectly: customUpload.uploadFileDirectly,
    uploadImagesDirectly: customUpload.uploadImagesDirectly,
  };
};

export { useFingeringUpload };
